import { Send, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { type TransactionNotesProps } from '../../types/transactions'
import { formatDate } from '../../lib/transaction-utils'

export function TransactionNotes({
  notes,
  newNote,
  onNewNoteChange,
  onSendNote,
  isSendingNote,
  isFlagged,
  onFlagToggle,
  isFlagging,
  showFlagToggle = false,
}: TransactionNotesProps) {
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSendNote()
    }
  }

  const getRole = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin'
      case 'GROUP_OWNER':
        return 'Group Owner'
      case 'COMPLIANCE':
        return 'Compliance'
      case 'TRADER':
        return 'Trader'
      default:
        return role
    }
  }

  return (
    <Card className="h-full flex flex-col max-h-[calc(100vh-380px)]">
      <CardHeader className="flex flex-row items-center justify-between flex-shrink-0">
        <CardTitle>Notes</CardTitle>
        {showFlagToggle && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Flag tx:</span>
            <label
              className={`relative inline-flex items-center ${isFlagging ? 'cursor-wait' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isFlagged}
                disabled={isFlagging}
                onChange={(e) => onFlagToggle(e.target.checked)}
              />
              <div
                className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-destructive rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-destructive ${isFlagging ? 'opacity-50' : ''}`}
              ></div>
            </label>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-y-auto">
        {showFlagToggle && (
          <>
            {isFlagged ? (
              <div className="w-full bg-destructive/10 border border-destructive text-destructive text-center py-2 rounded-md mb-4 font-medium">
                Flagged
              </div>
            ) : (
              <div className="w-full bg-gray-100 text-gray-800 text-center py-2 rounded-md mb-4 font-medium">
                Clear
              </div>
            )}
          </>
        )}

        {/* Add Note Input */}
        <div className="mb-4">
          <div className="relative">
            <textarea
              value={newNote}
              onChange={(e) => onNewNoteChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message"
              className="w-full min-h-[60px] max-h-[120px] p-3 pr-12 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-main focus:border-transparent"
              rows={2}
              disabled={isSendingNote}
            />
            <Button
              onClick={onSendNote}
              disabled={!newNote.trim() || isSendingNote}
              className="absolute right-2 bottom-4 h-8 w-8 p-0 bg-main/90 hover:bg-main disabled:bg-gray-300"
              size="sm"
            >
              {isSendingNote ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {notes && notes.length > 0 ? (
            notes
              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
              .map((note) => (
                <div key={note.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{note.author}</span>
                      <span className="text-sm text-card-text">{getRole(note.role)}</span>
                    </div>
                    <span className="text-xs text-card-text">{formatDate(note.timestamp)}</span>
                  </div>
                  <p className="text-sm">{note.content}</p>
                </div>
              ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              No notes yet. Add the first note below.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
